import React, { Component, ErrorInfo, ReactNode } from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import crashlytics from "@react-native-firebase/crashlytics";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

function isModuleScriptLoadError(message: string | undefined): boolean {
  if (!message) return false;
  return message.includes("Loading module") && message.includes(".js failed");
}

export class ErrorBoundary extends Component<Props, State> {
  private moduleLoadAutoRetryDone = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Crashlytics with full stack trace
    this.logErrorToCrashlytics(error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });

    if (
      isModuleScriptLoadError(error.message) &&
      typeof window !== "undefined" &&
      !this.moduleLoadAutoRetryDone
    ) {
      const params = new URLSearchParams(window.location.search);
      if (!params.has("reloadTime")) {
        this.moduleLoadAutoRetryDone = true;
        queueMicrotask(() => this.handleRetry());
      }
    }
  }

  private safeLog(label: string, value?: any) {
    if (value === undefined || value === null) return;

    let formattedValue: string;

    try {
      if (typeof value === "string") {
        formattedValue = value;
      } else if (value instanceof Error) {
        formattedValue = `${value.name}: ${value.message}\n${value.stack}`;
      } else if (typeof value === "object") {
        formattedValue = JSON.stringify(value);
      } else {
        formattedValue = String(value);
      }
    } catch {
      formattedValue = "[Unserializable]";
    }

    crashlytics().log(`${label}: ${formattedValue.slice(0, 1000)}`);
  }

  private logErrorToCrashlytics = (error: Error, errorInfo: ErrorInfo) => {
    try {
      // Set error context
      crashlytics().setAttribute("error_boundary", "true");
      crashlytics().setAttribute(
        "error_component",
        errorInfo.componentStack || "unknown"
      );
      crashlytics().setAttribute("error_type", "js_error_boundary");

      // Log the error with stack trace
      crashlytics().recordError(new Error(error?.message || "Unknown error"));

      // Log additional context
      this.safeLog("Error Boundary Caught", error?.message);
      this.safeLog("Component Stack", errorInfo?.componentStack);

      // Log error details as attributes
      crashlytics().setAttribute("error_message", error.message);
      crashlytics().setAttribute("error_name", error.name);
      crashlytics().setAttribute("error_stack", error.stack || "no_stack");

      console.log("Error logged to Crashlytics:", error);
    } catch (crashlyticsError) {
      console.error("Failed to log error to Crashlytics:", crashlyticsError);
    }
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (!params.has("reloadTime")) {
        const separator = window.location.search ? "&" : "?";
        window.location.href =
          window.location.href + separator + "reloadTime=" + Date.now();
      } else {
        window.location.reload();
      }
    }
  };

  private handleReportError = () => {
    if (this.state.error) {
      // Log additional user action
      crashlytics().log("User reported error manually");
      crashlytics().setAttribute("user_reported", "true");
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <View style={styles.errorContainer}>
          <Text style={styles.errorTitle}>Something went wrong</Text>
          <Text style={styles.errorMessage}>
            {this.state.error?.message || "An unexpected error occurred"}
          </Text>

          {__DEV__ && this.state.errorInfo && (
            <View style={styles.debugInfo}>
              <Text style={styles.debugTitle}>Debug Information:</Text>
              <Text style={styles.debugText}>
                Component: {this.state.errorInfo.componentStack}
              </Text>
              <Text style={styles.debugText}>
                Stack: {this.state.error?.stack}
              </Text>
            </View>
          )}

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.retryButton}
              onPress={this.handleRetry}
            >
              <Text style={styles.buttonText}>Try Again</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.reportButton}
              onPress={this.handleReportError}
            >
              <Text style={styles.buttonText}>Report Error</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    backgroundColor: "#f8f9fa",
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#dc3545",
    marginBottom: 10,
    textAlign: "center",
  },
  errorMessage: {
    fontSize: 16,
    color: "#6c757d",
    textAlign: "center",
    marginBottom: 20,
  },
  debugInfo: {
    backgroundColor: "#e9ecef",
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
    width: "100%",
  },
  debugTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#495057",
    marginBottom: 8,
  },
  debugText: {
    fontSize: 12,
    color: "#6c757d",
    marginBottom: 4,
    fontFamily: "monospace",
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 15,
  },
  retryButton: {
    backgroundColor: "#007bff",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 6,
  },
  reportButton: {
    backgroundColor: "#6c757d",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 6,
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default ErrorBoundary;
